import { Share2 } from "lucide-react";
import { toast } from "sonner";
import { SLIDES_META } from "@/components/OfferBanner";

// Lien de partage de la maison : page balisée côté serveur (image d'aperçu dans SMS/WhatsApp…)
export const producerSlug = (name) =>
  name.normalize("NFD").replace(/[̀-ͯ]/g, "").toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "");

export const producerShareUrl = (name) => `${window.location.origin}/api/share/${producerSlug(name)}`;

export async function shareProducer(name) {
  const meta = SLIDES_META[name];
  const url = producerShareUrl(name);
  const title = `${name} — Les Vins du Bistroquet`;
  const text = meta?.tagline ? `${meta.tagline}` : `Découvrez la sélection ${name} à la cave du Bistroquet`;
  if (navigator.share) {
    try {
      await navigator.share({ title, text, url });
      return;
    } catch {
      return; // partage annulé par l'utilisateur
    }
  }
  try {
    await navigator.clipboard.writeText(url);
    toast.success("Lien copié — collez-le dans un SMS, WhatsApp, Facebook…");
  } catch {
    toast.error(`Copiez ce lien : ${url}`);
  }
}

// Bouteilles présentées entières dans la plaquette Billecart-Salmon (halo intégré, jamais recadrées)
const LEAFLET_BOTTLES = {
  "Billecart-Salmon": [
    "/assets/wines/hd/billecart-sous-bois.png",
    "/assets/wines/hd/billecart-blanc-de-blancs.png",
    "/assets/wines/hd/billecart-rose.png",
    "/assets/wines/hd/billecart-reserve.png",
  ],
  "Les Vins de Vienne": [
    "/assets/wines/hd/vienne-taburnum.png",
    "/assets/wines/hd/vienne-amphore-or.png",
    "/assets/wines/hd/vienne-essartailles.png",
    "/assets/wines/hd/vienne-sotanum.png",
  ],
  "Domaine Gayda": [
    "/assets/wines/hd/gayda-moscou.png",
  ],
  "Mas de Daumas Gassac": [
    "/assets/wines/hd/daumas-blanc.png",
    "/assets/wines/hd/daumas-rouge.png",
  ],
  "Mas des Armes": [
    "/assets/wines/hd/mda-perspectives.png",
    "/assets/wines/hd/mda-360-blanc.png",
    "/assets/wines/hd/mda-360-rouge.png",
  ],
  "Domaine Lafage": [
    "/assets/wines/hd/lafage-retro.png",
  ],
  "Deutz": [
    "/assets/wines/hd/deutz-brut.png",
    "/assets/wines/hd/deutz-amour.png",
  ],
  "Beaujolais Nouveau": [
    "/assets/wines/hd/piron-nouveau.png",
  ],
  "Domaine du Vieux Télégraphe": [
    "/assets/wines/hd/vt-lacrau.png",
    "/assets/wines/hd/vt-telegramme.png",
    "/assets/wines/hd/vt-megaphone.png",
  ],
};

// Fond des plaquettes avec bouteilles : dégradé propre à chaque maison
const LEAFLET_BACKGROUNDS = {
  "Billecart-Salmon": "linear-gradient(100deg, #161111 25%, #4A101C 100%)",
  "Les Vins de Vienne": "linear-gradient(100deg, #121615 25%, #34443E 100%)",
  "Domaine Gayda": "linear-gradient(100deg, #161011 25%, #4A161A 100%)",
  "Mas de Daumas Gassac": "linear-gradient(100deg, #101418 25%, #26343F 100%)",
  "Mas des Armes": "linear-gradient(100deg, #14100C 25%, #382A1E 100%)",
  "Domaine Lafage": "linear-gradient(100deg, #14100A 25%, #423212 100%)",
  "Deutz": "linear-gradient(100deg, #12100B 25%, #342C1C 100%)",
  "Beaujolais Nouveau": "linear-gradient(100deg, #140C12 25%, #461432 100%)",
  "Domaine du Vieux Télégraphe": "linear-gradient(100deg, #130E0B 25%, #3E2219 100%)",
};

// Petite plaquette publicitaire affichée au-dessus de chaque maison dans la cave.
// Inspirée du site officiel de la maison (emblème, devise, bouteilles).
// Une nouvelle maison ajoutée dans SLIDES_META obtient automatiquement sa plaquette ;
// si elle a des bouteilles dans LEAFLET_BOTTLES, elles sont présentées entières à droite.
export default function ProducerLeaflet({ producer, onClick }) {
  const meta = SLIDES_META[producer];
  if (!meta) return null;
  const bottles = LEAFLET_BOTTLES[producer];
  return (
    <div className="relative">
      <button
        onClick={() => shareProducer(producer)}
        className="absolute top-2 right-2 z-10 rounded-full bg-[#1C1917]/55 backdrop-blur-sm text-[#FAF6F0] p-2 hover:bg-[#6B1226] transition-colors"
        data-testid={`producer-share-${producerSlug(producer)}`}
        aria-label={`Partager ${producer}`}
        title={`Partager ${producer} (SMS, WhatsApp…)`}
      >
        <Share2 size={15} />
      </button>
      <button
        onClick={onClick}
        className="relative w-full overflow-hidden rounded-2xl text-left cursor-pointer block group"
        data-testid={`producer-leaflet-${producer.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
        aria-label={`Voir la sélection ${producer}`}
      >
      {bottles ? (
        <div
          className="relative w-full h-28 sm:h-32"
          style={{ background: LEAFLET_BACKGROUNDS[producer] || "linear-gradient(100deg, #161111 25%, #4A101C 100%)" }}
        >
          <div
            className="absolute inset-0"
            style={{ background: "radial-gradient(ellipse 45% 90% at 78% 45%, rgba(197,160,89,0.28), transparent 70%)" }}
          />
          <div className="absolute inset-0 flex items-center gap-3 sm:gap-4 px-4 sm:px-6">
            {meta.emblem && (
              <img
                src={meta.emblem}
                alt={`Emblème ${producer}`}
                className="h-10 sm:h-12 w-auto object-contain object-left no-frame shrink-0"
                data-testid="producer-leaflet-emblem"
              />
            )}
            <div className="min-w-0">
              <p className="font-display text-lg sm:text-xl font-semibold text-[#FAF6F0] leading-tight truncate">{producer}</p>
              <p className="text-[0.65rem] sm:text-xs text-[#E8D8B0] mt-0.5 tracking-wide leading-snug">{meta.tagline}</p>
            </div>
          </div>
          <div className="absolute right-3 sm:right-6 top-0 bottom-0 flex items-end pointer-events-none" data-testid="producer-leaflet-bottles">
            {bottles.map((src, i) => (
              <img
                key={src}
                src={src}
                alt=""
                className={`h-[86%] w-auto object-contain no-frame transition-transform duration-500 group-hover:-translate-y-0.5 ${i > 0 ? "-ml-3 sm:-ml-2" : ""} ${i === 0 ? "hidden min-[420px]:block" : ""}`}
              />
            ))}
          </div>
        </div>
      ) : (
        <>
          <img
            src={meta.image}
            alt={meta.title}
            className="w-full h-24 sm:h-28 object-cover no-frame transition-transform duration-500 group-hover:scale-[1.03]"
            style={{ objectPosition: "center 45%" }}
          />
          <div className="absolute inset-0 flex items-center gap-3 sm:gap-4 px-4 sm:px-6 bg-gradient-to-r from-[#1C1917]/85 via-[#1C1917]/45 to-transparent">
            {meta.emblem && (
              <img
                src={meta.emblem}
                alt={`Emblème ${producer}`}
                className="h-10 sm:h-12 w-auto object-contain object-left no-frame shrink-0"
                data-testid="producer-leaflet-emblem"
              />
            )}
            <div className="min-w-0">
              <p className="font-display text-lg sm:text-xl font-semibold text-[#FAF6F0] leading-tight truncate">{producer}</p>
              <p className="text-[0.65rem] sm:text-xs text-[#E8D8B0] mt-0.5 tracking-wide leading-snug">{meta.tagline}</p>
            </div>
          </div>
        </>
      )}
      </button>
    </div>
  );
}
